import './App.css';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Center, Spinner } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { Sidebar } from './components/Sidebar';
import { TabBar } from './components/TabBar';
import { Dashboard } from './pages/Dashboard';
import { AIWorkspace } from './pages/AIWorkspace';
import { Library } from './pages/Library';
import { Collection } from './pages/Collection';
import { NotePage } from './pages/NotePage';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Logout } from './pages/Logout';
import { Scanner } from './pages/Scanner';
import { Subscription } from './pages/Subscription';
import { SubscriptionSuccess } from './pages/SubscriptionSuccess';
import { Checkout } from './pages/Checkout';
import { Account } from './pages/Account';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Pricing } from './pages/Pricing';
import { Refunds } from './pages/Refunds';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RequireSubscription } from './components/RequireSubscription';
import { Toaster } from './components/ui';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Root path resolver. Logged-in users go to /dashboard; logged-out users
 * shouldn't ever reach this (backend serves the marketing site at /), but if
 * they do — proxy misconfigured, etc. — we show a spinner and leave a hard
 * redirect to / queued, without unconditionally re-navigating into a loop.
 */
function RootRedirect() {
  const { user, loading } = useAuth();
  useEffect(() => {
    if (loading || user) return;
    const FLAG = 'pb:marketing-fallback-attempted';
    if (sessionStorage.getItem(FLAG)) return;
    sessionStorage.setItem(FLAG, '1');
    window.location.replace('/');
  }, [loading, user]);

  if (user) return <Navigate to="/dashboard" replace />;
  return (
    <Center h="50vh">
      <Spinner size="lg" color="var(--accent)" />
    </Center>
  );
}

/**
 * Layout shell — the floating Sidebar on desktop, mobile's floating pill TabBar
 * on phone widths; page content renders via <Outlet />.
 */
function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const isSidebarCollapsed = location.pathname === '/workspace';

  const mainClass = user
    ? isSidebarCollapsed ? 'with-sidebar-collapsed' : 'with-sidebar'
    : '';

  return (
    <div className="App">
      {user && (
        <>
          <Sidebar collapsed={isSidebarCollapsed} />
          <TabBar />
        </>
      )}
      <main className={`main-content ${mainClass}`}>
        <div className="content-area">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* Once, at the root: every route's toaster.* calls render here. It lived in a
            layout wrapper that was deleted in April, which silently muted all toasts. */}
        <Toaster />
        <Router>
          <Routes>
            {/* No-shell routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/public/note/:noteId" element={<NotePage />} />

            {/* Public legal pages — load regardless of auth state */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refunds" element={<Refunds />} />

            {/* Public pricing — read live from Paddle via /api/payments/plans */}
            <Route path="/pricing" element={<Pricing />} />

            {/* Paddle's default payment link — public, Paddle.js opens ?_ptxn= itself */}
            <Route path="/checkout" element={<Checkout />} />

            {/* Subscription routes — require auth but bypass the subscription gate */}
            <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />

            {/* Shell routes — sidebar + layout, require active subscription */}
            <Route element={<AppShell />}>
              <Route path="/dashboard" element={<RequireSubscription><Dashboard /></RequireSubscription>} />
              <Route path="/workspace" element={<RequireSubscription><AIWorkspace /></RequireSubscription>} />
              <Route path="/library" element={<RequireSubscription><Library /></RequireSubscription>} />
              <Route path="/library/:collectionId" element={<RequireSubscription><Collection /></RequireSubscription>} />
              <Route path="/note/:noteId" element={<RequireSubscription><NotePage /></RequireSubscription>} />
              <Route path="/scanner" element={<RequireSubscription><Scanner /></RequireSubscription>} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

              <Route path="/" element={<RootRedirect />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
